import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import { markCommentReportAsViewedAPI, createAppealAPI, getFirstUnviewedDeletedCommentAPI } from "../../api/userApi";
import { showToast } from "../../utils/toast";
import { X } from "lucide-react";
import {
  Modal,
  Box,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { useStyles } from "./DeletedPostModal";
import { Favorite } from "@material-ui/icons";
import CommentText from "./comment-text";
import { useProfileStyles } from "../Header/ProfileViewer/profile-viewer.styles";

const DeletedCommentModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const classes = useStyles();
  const classesForComments = useProfileStyles({ variant: "viewer" });
  const [loading, setLoading] = useState(false);
  const [commentReport, setCommentReport] = useState<any>(null);
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [appealComment, setAppealComment] = useState("");
  const [submittingAppeal, setSubmittingAppeal] = useState(false);
  const fetchedOnceRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchUnviewedComment = async () => {
    setLoading(true);
    try {
      const response = await getFirstUnviewedDeletedCommentAPI();
      if (response.success && response.data) {
        setCommentReport(response.data);
      } else {
        setCommentReport(null);
      }
    } catch (error) {
      console.error("Error fetching deleted comment:", error);
      setCommentReport(null);
    } finally {
      setLoading(false);
    }
  };

  // Закриття модалки
  const handleClose = async () => {
    if (commentReport && !showAppealForm) {
      try {
        await markCommentReportAsViewedAPI(commentReport._id);
      } catch (error) {
        console.error("Error marking comment as viewed:", error);
      }
    }
    setShowAppealForm(false);
    setAppealComment("");
    setCommentReport(null);
    fetchedOnceRef.current = false;
    onClose();
  };

  // Показати форму апеляції
  const handleShowAppealForm = () => setShowAppealForm(true);

  const handleCancelAppeal = () => {
    setShowAppealForm(false);
    setAppealComment("");
  };

  // Відправка апеляції
  const handleSubmitAppeal = async () => {
    if (!commentReport || !appealComment.trim()) {
      showToast("Please provide a comment for your appeal", "error");
      return;
    }
    try {
      setSubmittingAppeal(true);
      const response = await createAppealAPI(
        commentReport.comment._id,
        "comment",
        "Appeal",
        appealComment.trim()
      );
      if (response.success) {
        showToast("Appeal submitted successfully", "success");
        try {
          await markCommentReportAsViewedAPI(commentReport._id);
        } catch (error) {
          console.error("Error marking comment as viewed:", error);
        }
        handleClose();
      } else {
        showToast(response.message || "Failed to submit appeal", "error");
      }
    } catch (error: any) {
      showToast(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit appeal",
        "error"
      );
    } finally {
      setSubmittingAppeal(false);
    }
  };


  const formatCommentTime = (createdAt: string): string => {
    const now = Date.now();
    const commentDate = new Date(createdAt).getTime();
    const diffMs = now - commentDate;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return "now";
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    if (diffWeeks < 4) return `${diffWeeks}w`;
    if (diffMonths < 12) return `${diffMonths}m`;
    return `${diffYears}y`;
  };

  useEffect(() => {
    if (open && !fetchedOnceRef.current) {
      fetchedOnceRef.current = true;
      fetchUnviewedComment();
    }
  }, [open]);

  return (
    <Modal
      open={open}
      disablePortal
      disableEnforceFocus
      disableScrollLock
      BackdropProps={{
        onClick: handleClose,
        style: { backgroundColor: "rgba(0, 0, 0, 0.7)" }
      }}
      className={classes.modalSubscriptionWrapper}
    >
      <Box
        className={`${classes.modalWrapper} ${showAppealForm ? classes.modalWrapperAppeal : ""}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        style={isMobile ? {} : { padding: "20px 16px" }}
      >
        {/* Header */}
        <Box className={classes.header}>
          {showAppealForm ? (
            <Typography className={classes.appealTitle}>Fill an appeal</Typography>
          ) : (
            <Box />
          )}
          <IconButton onClick={handleClose} className={classes.closeButton} size="small">
            <X size={24} />
          </IconButton>
        </Box>

        <DialogContent className={classes.content}>
          {loading ? (
            <Box className={classes.loadingContainer}><CircularProgress /></Box>
          ) : !commentReport ? (
            <Box className={classes.emptyContainer}>
              <Typography variant="h6" style={{ color: "#999" }}>
                No deleted comments to review
              </Typography>
            </Box>
          ) : showAppealForm ? (
            <Box className={classes.appealForm}>
              <Typography className={classes.appealInfo}>
                Think your comment was removed by mistake? We’ll review it again and restore if it doesn’t violate{" "}
                <span
                  className={classes.rulesLink}
                  onClick={() => {
                    handleClose();
                    window.location.href = "/terms-of-service";
                  }}
                >
                  our rules
                </span>.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={appealComment}
                onChange={(e: any) => setAppealComment(e.target.value)}
                placeholder="Explain why your comment should be restored..."
                className={classes.inputNoBorder}
                InputProps={{
                  style: {
                    color: "#fff",
                    backgroundColor: "#1F1F1F",
                    fontSize: "16px",
                    border: "1px solid #747474",
                    borderRadius: "8px"
                  }
                }}
                inputProps={{ maxLength: 200 }}
              />
              <Typography variant="caption" style={{ color: "#FFFFFFCC", marginTop: 4, textAlign: "right" }}>
                {appealComment.length}/200 characters
              </Typography>
            </Box>
          ) : (
            <>
              {commentReport &&
                <Box mt={2} p={2} textAlign={"left"} style={{ backgroundColor: "#1F1F1F80", maxWidth: "550px", margin: "auto", borderRadius: "6px", marginBottom: 40 }}>
                  <div className={classesForComments.commentItem} style={{ marginBottom: 0, padding: 0 }}>
                    <Avatar
                      src={commentReport.comment.author.profileImg || "/images/defaultImage/profile.png"}
                      style={{ width: 48, height: 48 }}
                    />
                    <div className={classesForComments.commentBody}>
                      <Typography
                        className={classesForComments.commentAuthor}
                        style={{
                          textDecoration: "none",
                          cursor: "pointer",
                        }}
                      >
                        {commentReport.comment.author.username}
                      </Typography>
                      <CommentText
                        text={commentReport.comment.text}
                        className={classesForComments.commentText}
                        onMentionClick={() => { }}
                      />
                      <div className={classesForComments.commentFooter}>
                        <Typography
                          variant="caption"
                          className={classesForComments.commentFooterText}
                        >
                          {formatCommentTime(commentReport.createdAt)}
                        </Typography>
                        <span className={classesForComments.dot}></span>
                        <Typography
                          variant="caption"
                          className={classesForComments.commentReplyButton}
                          onClick={() => { }}
                        >
                          Reply
                        </Typography>
                      </div>
                    </div>

                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <IconButton
                        style={{ padding: 2 }}
                      >
                        <Favorite
                          fontSize="small"
                          style={{
                            height: 20,
                            width: 20,
                            color: commentReport.comment.likesCount > 0 ? "#FE5A97" : "transparent",
                            stroke: commentReport.comment.likesCount > 0 ? "none" : "white",
                            strokeWidth: commentReport.comment.likesCount > 0 ? 0 : 1,
                            transition: "color 0.3s",
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                      <Typography
                        variant="caption"
                        style={{ margin: 0, fontSize: 14 }}
                      >
                        {commentReport.comment.likesCount === 0 ? "" : commentReport.comment.likesCount}
                      </Typography>
                    </Box>
                  </div>
                </Box>}
              <Typography className={classes.deletedMessage}>Your comment was deleted</Typography>
              <Typography className={classes.deletedInfo}>
                This comment was taken down for violating our rules.
                Reason: {commentReport?.reason || ""}
                <br /><br />
                If you think it was removed in error, you can appeal the decision and our team will take another look.
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions className={classes.actions} style={!showAppealForm ? { flexWrap: "nowrap", gap: 60, padding: "0 16px 24px" } : { flexWrap: "wrap", gap: 8, padding: "32px 16px 24px" }}>
          {showAppealForm ? (
            <>
              <Button onClick={handleCancelAppeal}
                className={clsx(classes.cancelButton, classes.smallActionBtn)}
                style={isMobile ? { minWidth: "100%", width: "100%" } : {}}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAppeal}
                className={clsx(classes.appealButton, classes.smallActionBtn)}
                disabled={submittingAppeal || !appealComment.trim()}
                style={isMobile ? { minWidth: "100%" } : {}}
              >
                {submittingAppeal ? "Submitting..." : "Submit"}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleShowAppealForm} className={classes.appealButton} style={isMobile ? { minWidth: "max-content" } : {}}>
                Appeal decision
              </Button>
              <Button onClick={handleClose} className={classes.cancelButton} style={isMobile ? { minWidth: "max-content" } : {}}>
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Box>
    </Modal >
  );
};

export default DeletedCommentModal;
